import React, { useEffect, useState } from "react";

interface Props {
    fromRef: React.RefObject<HTMLElement>;
    toRef: React.RefObject<HTMLElement>;
    color?: string;
    thickness?: number;
}

// const LineConnector: React.FC<Props> = ({ fromRef, toRef, color = "#1a3d9f", thickness = 2 }) => {
//     const [lineStyle, setLineStyle] = useState<React.CSSProperties>({});

//     useEffect(() => {
//         const updateLine = () => {
//             if (!fromRef.current || !toRef.current) return;

//             const from = fromRef.current.getBoundingClientRect();
//             const to = toRef.current.getBoundingClientRect();

//             const container = document.getElementById("line-container");
//             if (!container) return;

//             const containerRect = container.getBoundingClientRect();

//             const x1 = from.left + from.width / 2 - containerRect.left;
//             const y1 = from.top + from.height / 2 - containerRect.top;
//             const x2 = to.left + to.width / 2 - containerRect.left;
//             const y2 = to.top + to.height / 2 - containerRect.top;

//             const length = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
//             const angle = Math.atan2(y2 - y1, x2 - x1) * (180 / Math.PI);

//             const style: React.CSSProperties = {
//                 position: "absolute",
//                 top: y1,
//                 left: x1,
//                 width: length,
//                 height: thickness,
//                 backgroundColor: color,
//                 transform: `rotate(${angle}deg)`,
//                 transformOrigin: "0 0",
//                 zIndex: 0,
//             };

//             setLineStyle(style);
//         };

//         updateLine();
//         window.addEventListener("resize", updateLine);
//         window.addEventListener("scroll", updateLine, true);

//         return () => {
//             window.removeEventListener("resize", updateLine);
//             window.removeEventListener("scroll", updateLine, true);
//         };
//     }, [fromRef, toRef]);

//     return <div style={lineStyle}></div>;
// };



type Anchor =
    | "center"
    | "topLeft"
    | "topRight"
    | "bottomLeft"
    | "bottomRight"
    | "top"
    | "bottom"
    | "left"
    | "right";

interface Props {
    fromRef: React.RefObject<HTMLElement>;
    toRef: React.RefObject<HTMLElement>;
    containerRef: React.RefObject<HTMLElement>;
    color?: string;
    thickness?: number;
}

const getAnchorFromAttr = (el: HTMLElement | null, type: string): Anchor => {
    if (!el) return "center";
    if (type == 'from' && el.hasAttribute("data-from")) return (el.getAttribute("data-from") as Anchor) || "center";
    return (el.getAttribute("data-anchor") as Anchor) || "center";
};

const getAnchorPoint = (rect: DOMRect, anchor: Anchor) => {
    const offset = 30; // Adjust this value to change the offset from the edge
    switch (anchor) {
        case "topLeft": return { x: rect.left, y: rect.top };
        case "topRight": return { x: rect.right, y: rect.top };
        case "bottomLeft": return { x: rect.left, y: rect.bottom };
        case "bottomRight": return { x: rect.right, y: rect.bottom };
        case "top":
            return { x: rect.left + offset, y: rect.top };
        case "bottom":
            return { x: rect.left + offset, y: rect.bottom + offset };
        case "left":
            return { x: rect.left, y: rect.top + offset };
        case "right":
            return { x: rect.right, y: rect.top + offset };
        case "center":
        default:
            return {
                x: rect.left + rect.width / 2,
                y: rect.top + rect.height / 2,
            };
    }
};

const LineConnector: React.FC<Props> = ({
    fromRef,
    toRef,
    containerRef,
    color = "#1a3d9f",
    thickness = 2,
}) => {
    const [lineStyle, setLineStyle] = useState<React.CSSProperties>({});

    useEffect(() => {
        const updateLine = () => {
            requestAnimationFrame(() => {
                if (!fromRef.current || !toRef.current || !containerRef.current) return;

                const fromEl = fromRef.current;
                const toEl = toRef.current;
                const containerRect = containerRef.current.getBoundingClientRect();

                const fromRect = fromEl.getBoundingClientRect();
                const toRect = toEl.getBoundingClientRect();

                const fromAnchor = getAnchorFromAttr(fromEl, "from");
                const toAnchor = getAnchorFromAttr(toEl, "to");

                const fromPoint = getAnchorPoint(fromRect, fromAnchor);
                const toPoint = getAnchorPoint(toRect, toAnchor);

                const x1 = fromPoint.x - containerRect.left;
                const y1 = fromPoint.y - containerRect.top;
                const x2 = toPoint.x - containerRect.left;
                const y2 = toPoint.y - containerRect.top;

                const length = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
                const angle = Math.atan2(y2 - y1, x2 - x1) * (180 / Math.PI);

                const style: React.CSSProperties = {
                    position: "absolute",
                    top: y1,
                    left: x1,
                    width: length,
                    height: thickness,
                    backgroundColor: color,
                    transform: `rotate(${angle}deg)`,
                    transformOrigin: "0 0",
                    zIndex: 0,
                    pointerEvents: "none",
                };

                setLineStyle(style);
            });
        };

        const observer = new MutationObserver(() => {
            updateLine();
        });

        if (containerRef.current) {
            observer.observe(containerRef.current, {
                childList: true,
                subtree: true,
            });
        }

        updateLine();
        window.addEventListener("resize", updateLine);
        window.addEventListener("scroll", updateLine, true);

        return () => {
            window.removeEventListener("resize", updateLine);
            window.removeEventListener("scroll", updateLine, true);
        };
    }, [fromRef, toRef, containerRef, color, thickness]);

    return <div style={lineStyle}></div>;
};


export default LineConnector;
